import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { buildProviderSlug } from "@/lib/provider-slug";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  MapPin,
  Phone,
  Star,
  Languages as LanguagesIcon,
  CalendarCheck,
} from "lucide-react";
import { findInsurance, type Provider } from "@/data/heallexa";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function nextAvailableLabel(availableDays: string[]): string {
  if (!availableDays || availableDays.length === 0) return "Availability on request";
  const set = new Set(availableDays.map((d) => d.toLowerCase().slice(0, 3)));
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const key = DAY_KEYS[d.getDay()];
    if (set.has(key)) {
      if (i === 0) return "Next available: Today";
      if (i === 1) return "Next available: Tomorrow";
      return `Next available: ${DAY_LABELS[d.getDay()]}`;
    }
  }
  return "Availability on request";
}

function NextAvailable({ days }: { days: string[] }) {
  const [label, setLabel] = useState<string>("Check availability");
  useEffect(() => { setLabel(nextAvailableLabel(days)); }, [days]);
  return <>{label}</>;
}

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "fill-muted text-muted-foreground/30"}`}
          aria-hidden
        />
      ))}
    </div>
  );
}

export function ProviderCard({ p, highlights = [] }: { p: Provider; highlights?: string[] }) {
  const claimed = p.listing_status !== "unclaimed";
  const identityVerified = !!(p as any).verified_badge;
  const premium = p.listing_status === "premium";
  const reviewsCount = Number((p as any).reviews_count ?? 0);
  const hasRealRating = reviewsCount > 0 && Number(p.rating) > 0;
  const rating = Number(p.rating) || 0;
  return (
    <article className="group relative grid cursor-pointer overflow-hidden rounded-md border border-border border-t-2 border-t-primary bg-card shadow-sm transition-shadow hover:shadow-md md:min-h-[280px] md:grid-cols-[300px_minmax(0,1fr)]">
      <Link
        to="/providers/$id"
        params={{ id: buildProviderSlug(p) }}
        aria-label={`View ${p.name} profile`}
        className="absolute inset-0 z-0"
      />

      {/* Portrait column */}
      <div className="relative aspect-[4/5] overflow-hidden bg-muted md:aspect-auto md:h-full md:min-h-[280px]">
        <ProviderPhoto provider={p} className="absolute inset-0 h-full w-full object-cover" />
      </div>

      <div className="relative z-10 flex min-w-0 flex-col overflow-hidden px-6 py-7 md:px-6 md:py-6 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/providers/$id"
                params={{ id: buildProviderSlug(p) }}
                className="inline-flex max-w-full items-center gap-2 text-[18px] font-semibold leading-tight tracking-tight hover:underline"
              >
                <span className="truncate">{p.name}</span>
              </Link>
              {identityVerified ? (
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400 font-medium rounded-sm border border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5">
                  <ShieldCheck className="h-3 w-3" /> Identity Verified
                </span>
              ) : claimed && (
                <span className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                  <CheckCircle2 className="h-3 w-3" /> Verified
                </span>
              )}
              {premium && (
                <span className="text-[10px] bg-foreground text-background rounded-sm px-1.5 py-0.5 font-medium">Premium</span>
              )}
            </div>
            <p className="text-[13px] text-muted-foreground mt-0.5 truncate">{p.practice} · {p.specialty}</p>
            {(p.address || p.city || p.state) && (
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                {[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}
              </p>
            )}
            {(() => { const npi = p.npi?.trim(); return npi && /^\d{10}$/.test(npi) ? (
              <a
                href={`https://npiregistry.cms.hhs.gov/provider-view/${npi}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={(e) => e.stopPropagation()}
                title="View NPI record on the NPPES registry"
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground rounded-sm border border-border px-1.5 py-0.5 hover:border-primary/40 hover:text-primary transition-colors"
              >
                NPI&nbsp;·&nbsp;{npi}
              </a>
            ) : null; })()}
          </div>
          <div className="flex shrink-0 items-center gap-2 pt-0.5">
            <Stars rating={hasRealRating ? rating : 0} />
            {hasRealRating ? (
              <>
                <span className="text-[13px] font-medium">{rating.toFixed(1)}</span>
                <span className="text-[12px] text-muted-foreground">({reviewsCount})</span>
              </>
            ) : (
              <span className="text-[12px] text-muted-foreground">No reviews</span>
            )}
          </div>
        </div>

        {/* Insurance chips — mirrors the practice-area chip block */}
        <div className="mt-3 overflow-hidden">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Insurance</p>
          <div className="flex max-h-[54px] flex-wrap gap-1.5 overflow-hidden">
            {p.insurance.length === 0 ? (
              <span className="text-[11px] text-muted-foreground">Not specified</span>
            ) : (
              <>
                {p.insurance.slice(0, 5).map((id) => {
                  const ins = findInsurance(id);
                  if (!ins) return null;
                  return (
                    <span key={id} className="inline-flex h-6 items-center rounded-sm border border-border bg-background px-2 text-[11px] leading-none">
                      {ins.short}
                    </span>
                  );
                })}
                {p.insurance.length > 5 && (
                  <span className="inline-flex h-6 items-center rounded-sm px-2 text-[11px] leading-none text-muted-foreground">
                    +{p.insurance.length - 5}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {highlights.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {highlights.slice(0, 4).map((h) => (
              <span key={h} className="inline-flex h-6 items-center rounded-sm bg-primary/10 px-2 text-[11px] leading-none text-primary font-medium">
                {h}
              </span>
            ))}
          </div>
        )}

        {/* Contact rows — 2-col grid, aligned across all cards */}
        <div className="mt-auto grid grid-cols-1 items-center gap-2 border-t border-border pt-3 text-[12px] sm:grid-cols-2">
          <div className="min-w-0">
            {p.phone ? (
              <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 hover:underline min-w-0">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{p.phone}</span>
              </a>
            ) : (
              <span className="flex items-center gap-2 text-muted-foreground min-w-0">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{[p.city, p.state].filter(Boolean).join(", ")}</span>
              </span>
            )}
          </div>
          <div className="min-w-0">
            <span className="flex items-center gap-2 min-w-0" suppressHydrationWarning>
              <CalendarCheck className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate"><NextAvailable days={p.available_days ?? []} /></span>
            </span>
          </div>
          {(p.languages?.length ?? 0) > 0 && (
            <div className="min-w-0 sm:col-span-2">
              <span className="flex items-center gap-2 min-w-0">
                <LanguagesIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{(p.languages ?? []).slice(0, 4).join(", ")}{(p.languages?.length ?? 0) > 4 ? "…" : ""}</span>
              </span>
            </div>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Link
            to="/providers/$id"
            params={{ id: buildProviderSlug(p) }}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-5 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Book online <ArrowRight className="h-4 w-4" />
          </Link>
          {!claimed && (
            <Link
              to="/providers/$id/claim"
              params={{ id: p.id }}
              className="inline-flex h-10 items-center justify-center rounded-md border border-border bg-background px-5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted"
            >
              Claim listing
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
